import React, { useState, useEffect, useRef } from "react";
import { generateClient } from "aws-amplify/data";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StripeProduct, fetchStripeProducts, createCheckoutSession } from "../api/stripeApi";
import { PLAN_NAME_TO_FEATURE_KEY, peekMarketingPrompt } from "../utils/marketingParams";
import { shouldShowConsentModal } from "../utils/termsAuditTrail";
import { getSubscriptionsFromStripe, SubscriptionItems, DuplicateSubscription } from "../api/stripeSubscription";
import SubscriptionManager from "../components/SubscriptionManager";
import { subscriptionsByOwner } from "../graphql/queries";
import { PageContainer, PageHeader } from "../components/AssetBreadCrumb";
import { PageBackButton } from "../components/PageChrome";
import { Skeleton, SkeletonGroup } from "../components/Skeleton";

const client = generateClient();

// Set to `false` to offer only monthly subscriptions and hide the Monthly/Yearly billing-interval toggle. Flip back to `true` to re-enable yearly plans and the toggle.
const ALLOW_YEARLY_BILLING = false;

interface DriftWarning {
  type: 'stripe_only' | 'db_only' | 'mismatch' | 'duplicate';
  productId: string;
  message: string;
}

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state: any) => state.user);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(
    ALLOW_YEARLY_BILLING && searchParams.get('interval') === 'year' ? 'year' : 'month'
  );
  // Guards the marketing-link auto-checkout so it fires at most once per mount.
  const autoCheckoutFired = useRef(false);

  // Subscription data from both sources
  const [stripeItems, setStripeItems] = useState<SubscriptionItems>({});
  const [driftWarnings, setDriftWarnings] = useState<DriftWarning[]>([]);
  const [subscriptionDataLoading, setSubscriptionDataLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  // Load subscription data when user is available
  useEffect(() => {
    if (user?.permissionDetails?.id) {
      loadSubscriptionData();
    }
  }, [user?.permissionDetails?.id]);

  // Auto-detect billing interval from active subscription
  useEffect(() => {
    if (!ALLOW_YEARLY_BILLING) return;
    if (stripeProducts.length === 0 || Object.keys(stripeItems).length === 0) return;
    const hasYearlySubscription = Object.values(stripeItems).some(item => {
      const product = stripeProducts.find(p => p.prices?.some(pr => pr.id === item.priceId));
      const price = product?.prices?.find(pr => pr.id === item.priceId);
      return price?.recurring?.interval === 'year';
    });
    if (hasYearlySubscription) setBillingInterval('year');
  }, [stripeProducts, stripeItems]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const products = await fetchStripeProducts();
      setStripeProducts(products);
    } catch (error) {
      console.error("Failed to load products:", error);
      setError("Failed to load subscription plans. Please try again later.");
    } finally {
      setProductsLoading(false);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      setSubscriptionDataLoading(true);

      // Fetch Stripe data
      const stripeResponse = await getSubscriptionsFromStripe().catch(err => {
        console.error("Failed to fetch from Stripe:", err);
        return null;
      });

      // Set Stripe items (source of truth for disabling buttons)
      const stripeItemsData = stripeResponse?.items || {};
      setStripeItems(stripeItemsData);

      // Fetch DB subscription using user ID from Redux
      let dbItemsData: SubscriptionItems | null = null;
      const userId = user?.permissionDetails?.id;
      if (userId) {
        try {
          const result = await client.graphql({
            query: subscriptionsByOwner,
            variables: { owner: userId, limit: 1 }
          });

          const dbSub = (result as any).data?.subscriptionsByOwner?.items?.[0];
          if (dbSub) {
            // Parse items if it's a string (AWSJSON)
            dbItemsData = typeof dbSub.items === 'string'
              ? JSON.parse(dbSub.items)
              : dbSub.items;
          }
        } catch (err) {
          console.error("Failed to fetch DB subscription:", err);
        }
      }

      // Detect drift between Stripe and DB, including duplicates
      detectDrift(stripeItemsData, dbItemsData, stripeResponse?.duplicates || []);

    } catch (error) {
      console.error("Failed to load subscription data:", error);
    } finally {
      setSubscriptionDataLoading(false);
    }
  };

  const detectDrift = (
    stripeData: SubscriptionItems,
    dbData: SubscriptionItems | null,
    duplicates: DuplicateSubscription[]
  ) => {
    const warnings: DriftWarning[] = [];
    const dbItems = dbData || {};

    // Check for duplicate subscriptions in Stripe (same product, multiple subscriptions)
    duplicates.forEach(dup => {
      warnings.push({
        type: 'duplicate',
        productId: dup.productId,
        message: `Duplicate subscription: "${dup.productName || dup.productId}" has multiple active subscriptions in Stripe`
      });
    });

    // Check for items in Stripe but not in DB
    Object.keys(stripeData).forEach(productId => {
      if (!dbItems[productId]) {
        warnings.push({
          type: 'stripe_only',
          productId,
          message: `Product "${stripeData[productId].productName || productId}" exists in Stripe but not in local database`
        });
      }
    });

    // Check for items in DB but not in Stripe
    Object.keys(dbItems).forEach(productId => {
      if (!stripeData[productId]) {
        warnings.push({
          type: 'db_only',
          productId,
          message: `Product "${dbItems[productId].productName || productId}" exists in local database but not in Stripe`
        });
      }
    });

    // Check for mismatches (e.g., different quantities or priceIds)
    Object.keys(stripeData).forEach(productId => {
      if (dbItems[productId]) {
        const stripeItem = stripeData[productId];
        const dbItem = dbItems[productId];

        if (stripeItem.priceId !== dbItem.priceId) {
          warnings.push({
            type: 'mismatch',
            productId,
            message: `Price mismatch for "${stripeItem.productName || productId}": Stripe has ${stripeItem.priceId}, DB has ${dbItem.priceId}`
          });
        }
      }
    });

    setDriftWarnings(warnings);
  };

  // Check if user is subscribed to a specific price (using Stripe as source of truth)
  const isPriceOwned = (productId: string, priceId: string | undefined): boolean => {
    const item = stripeItems[productId];
    if (!item || !priceId) return false;
    return item.priceId === priceId;
  };

  const handleSubscribe = async (productId: string, priceId: string) => {
    setLoading(true);
    setSelectedPlan(productId);

    try {
      const session = await createCheckoutSession({
        priceId: priceId,
      });

      if (session.url) {
        window.location.href = session.url;
      } else if (session.sessionId) {
        console.error("No checkout URL provided, but session ID exists:", session.sessionId);
        setError("Failed to get checkout URL. Please try again.");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setError("Failed to create checkout session. Please try again.");
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  // Marketing deep-link: if arriving with ?plan=<name> (e.g. from /?signup=true&plan=professional), auto-launch Stripe checkout for the matching plan once products are loaded. Falls through to showing the plans page for unknown plans, missing products, or already-owned plans. Gated only on productsLoading (always resolves); subscription data may lag behind a fresh signup, so the already-owned skip below is best-effort and only applies once that data is in. Crucially, we also wait until the blocking platform-consent modal is satisfied before redirecting to Stripe — otherwise the modal flashes and we yank the user off to payment before they've consented. shouldShowConsentModal() mirrors VerticalNav's own gate; when the user accepts, VerticalNav updates the audit trails in redux, this re-evaluates to false, and checkout fires.
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    if (productsLoading) return;

    const planName = searchParams.get('plan');
    if (!planName) return;

    // Hold until consent is cleared (also covers permissionDetails not yet loaded → trails undefined → show=true).
    const consent = shouldShowConsentModal(
      user?.permissionDetails?.termsAndConditionsAuditTrail,
      user?.permissionDetails?.privacyPolicyAuditTrail
    );
    if (consent.show) return;

    autoCheckoutFired.current = true;

    const featureKey = PLAN_NAME_TO_FEATURE_KEY[planName.toLowerCase()];
    const product = featureKey
      ? stripeProducts.find(p => {
          const key = p.metadata?.feature_key || p.metadata?.["feature-key"] || p.metadata?.featureKey;
          return key === featureKey;
        })
      : undefined;
    const price = product?.prices?.find(p => p.recurring?.interval === billingInterval)
      || product?.prices?.[0];

    // Already-owned skip is best-effort and only applies once subscription data is in.
    const alreadyOwned = !!product && !!price && !subscriptionDataLoading && isPriceOwned(product.id, price.id);

    if (product && price && !alreadyOwned) {
      // → Stripe → success URL (/home/ai-analyst) → chat consumes any stashed marketing prompt.
      handleSubscribe(product.id, price.id);
      return;
    }

    // Checkout won't run (unknown plan, missing product, or already owned). If a marketing prompt was stashed to follow checkout, deliver it to the chat now instead of leaving it dangling in storage; otherwise just show the plans page.
    if (peekMarketingPrompt()) {
      navigate('/home/ai-analyst');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productsLoading,
    subscriptionDataLoading,
    stripeProducts,
    searchParams,
    billingInterval,
    user?.permissionDetails?.termsAndConditionsAuditTrail,
    user?.permissionDetails?.privacyPolicyAuditTrail,
  ]);

  // Note: if the user cancels Stripe checkout (?canceled=true), we intentionally LEAVE any stashed marketing prompt in sessionStorage so it pre-fills the next time they reach the chat (Choice B). It is consumed one-shot there (consumeMarketingPrompt) and is naturally bounded to the browser tab.

  const formatPrice = (amount: number | null, currency = 'usd') => {
    if (!amount) return "Free";
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 0
    }).format(amount / 100);
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Account"
        title="Manage Your Subscriptions"
        description="Select the plan that fits your needs. Upgrade or downgrade at any time."
        action={
          <PageBackButton label="Back to Settings" onClick={() => navigate("/home/settings")} />
        }
      />

      {productsLoading || subscriptionDataLoading ? (
        <SkeletonGroup count={3} label="Loading plans" className="eai-card-grid">
          <div className="eai-card flex flex-col p-8">
            <Skeleton className="h-5 w-32" radius="md" />
            <Skeleton className="mt-4 h-3.5 w-full" />
            <Skeleton className="mt-2 h-3.5 w-2/3" />
            <Skeleton className="mt-6 h-10 w-28" radius="md" />
            <Skeleton className="mt-6 h-eai-control-lg w-full" radius="full" />
          </div>
        </SkeletonGroup>
      ) : (
        <>
          {error && (
            <div className="mb-5 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface p-4">
              <p className="font-inter text-eai-body-sm text-eai-danger-text">{error}</p>
            </div>
          )}

          {driftWarnings.length > 0 && (
            <div className="mb-5 rounded-eai-card border border-eai-border bg-eai-surface-sunken p-4">
              <h4 className="eai-card-title text-eai-warning-text">
                Subscription Data Sync Warning
              </h4>
              <ul className="mt-2 list-inside list-disc font-inter text-eai-body-sm text-eai-ink-subtle">
                {driftWarnings.map((warning, index) => (
                  <li key={index}>{warning.message}</li>
                ))}
              </ul>
            </div>
          )}

          {stripeProducts.length > 0 ? (
            <>
              {/* Billing Interval Toggle */}
              {ALLOW_YEARLY_BILLING && (
                <div className="mb-8 flex justify-center">
                  <div className="eai-seg">
                    <button
                      type="button"
                      onClick={() => setBillingInterval("month")}
                      className={`eai-seg-item ${billingInterval === "month" ? "eai-seg-item-active" : ""}`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingInterval("year")}
                      className={`eai-seg-item ${billingInterval === "year" ? "eai-seg-item-active" : ""}`}
                    >
                      Yearly
                    </button>
                  </div>
                </div>
              )}

              <div className="eai-card-grid">
                {stripeProducts.map((product) => {
                  const price = product.prices?.find(p => p.recurring?.interval === billingInterval)
                    || product.prices?.[0];
                  const featureKey = product.metadata?.feature_key || product.metadata?.["feature-key"] || product.metadata?.featureKey;
                  const isRecommended = featureKey === "analyst-pro"
                    || product.metadata?.popular === "true"
                    || product.name?.toLowerCase().includes("professional");
                  const owned = isPriceOwned(product.id, price?.id);

                  return (
                    <div
                      key={product.id}
                      className={`eai-card flex w-full min-w-0 flex-col p-6 ${
                        isRecommended ? "border-eai-cta-primary" : ""
                      }`}
                    >
                      {/* Reserved so every card's title lands on the same line — only once they share a row */}
                      <div className={isRecommended ? "mb-3 h-6" : "hidden sm:mb-3 sm:block sm:h-6"}>
                        {isRecommended && (
                          <span className="eai-tag-pill-sm border-eai-cta-primary bg-eai-cta-primary text-white">
                            Most Popular
                          </span>
                        )}
                      </div>

                      <h3 className="eai-h3-title">{product.name}</h3>

                      {/* Fixed-height description keeps the price rows aligned across cards */}
                      <div className="sm:min-h-[3.5rem]">
                        {product.description && (
                          <p className="eai-description-sm mt-1">{product.description}</p>
                        )}
                      </div>

                      {price && (
                        <div className="mt-6 flex items-start gap-x-1">
                          <span className="font-outfit text-4xl font-bold leading-none tracking-tight text-eai-ink">
                            {formatPrice(price.unit_amount)}
                          </span>
                          {price.recurring && (
                            <span className="mt-1 font-inter text-eai-body-sm leading-none text-eai-ink-faint">
                              /{price.recurring.interval === "month" ? "mo" : "yr"}
                            </span>
                          )}
                        </div>
                      )}

                      {owned ? (
                        <button disabled className="eai-btn-outline mt-6 w-full gap-2">
                          <svg className="h-4 w-4 text-eai-cta-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Subscribed
                        </button>
                      ) : (
                        <button
                          onClick={() => price && handleSubscribe(product.id, price.id)}
                          disabled={!price || (loading && selectedPlan === product.id)}
                          className="eai-btn-cta mt-6 w-full"
                        >
                          {loading && selectedPlan === product.id ? "Processing…" : "Subscribe"}
                        </button>
                      )}

                      {product.marketing_features?.length > 0 && (
                        <>
                          <p className="eai-meta-label mt-6">This includes</p>
                          <ul className="mt-3 flex flex-col gap-2.5">
                            {product.marketing_features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2.5">
                                <svg className="mt-0.5 h-4 w-4 shrink-0 text-eai-cta-primary" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <path d="M3 8.5 6.5 12 13 4.5" />
                                </svg>
                                <span className="font-inter text-eai-body-sm text-eai-ink-subtle">
                                  {feature.name}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Custom Solution — contact sales */}
                <div className="eai-card flex w-full min-w-0 flex-col p-6">
                  <div className="hidden sm:mb-3 sm:block sm:h-6" />

                  <h3 className="eai-h3-title">Custom Solution</h3>

                  <div className="sm:min-h-[3.5rem]">
                    <p className="eai-description-sm mt-1">
                      Don't see a plan that fits? Talk to us about a custom solution.
                    </p>
                  </div>

                  {/* Price-height spacer (text-4xl leading-none) so the action lines up with the other cards */}
                  <div className="mt-6 hidden h-9 sm:block" />

                  <a
                    href="mailto:support+sales@erode.ai?subject=Enterprise%20Plan%20Inquiry"
                    className="eai-btn-cta mt-6 w-full"
                  >
                    Contact us
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="eai-panel text-center">
              <p className="eai-description-sm">No subscription plans available at the moment.</p>
            </div>
          )}

          <div className="mt-8">
            <SubscriptionManager />
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default SubscriptionsPage;
